import React, { useState, useEffect, useCallback, useRef, useMemo, forwardRef, useImperativeHandle } from 'react';
import { Map, AdvancedMarker, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { db, functions } from '../../firebase';
import { doc, getDoc, updateDoc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { FiTrash2, FiRotateCcw, FiSave, FiTarget, FiMap, FiHome, FiCrosshair, FiInfo, FiUpload, FiImage, FiGlobe } from 'react-icons/fi';

const COURSE_COLORS = [
  { id: 'red', color: '#FF0000', label: 'Piros pálya' },
  { id: 'blue', color: '#0000FF', label: 'Kék pálya' },
  { id: 'green', color: '#008000', label: 'Zöld pálya' },
  { id: 'yellow', color: '#FFD700', label: 'Sárga pálya' },
  { id: 'purple', color: '#800080', label: 'Lila pálya' }
];

// SEGÉDFÜGGVÉNY: Távolság számítás (Haversine formula)
const calculateDistance = (lat1, lon1, lat2, lng2) => {
  const R = 6371e3; // Föld sugara méterben
  const phi1 = lat1 * Math.PI / 180;
  const phi2 = lat2 * Math.PI / 180;
  const deltaPhi = (lat2 - lat1) * Math.PI / 180;
  const deltaLambda = (lng2 - lon1) * Math.PI / 180;

  const a = Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
            Math.cos(phi1) * Math.cos(phi2) *
            Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Méterben
};

const CourseMapEditor = forwardRef(({
  competitionId,
  numberOfTargets,
  defaultLat,
  defaultLng,
  onDistanceChange,
  totalDistance,
  customMapImageUrl,
  isCustomMap,
  isPublishedMap,
  onSwitchToGoogleMaps,
  onRemoveCustomMap,
  onUploadNewCustomMap,
  isUploadingCustomMap
}, ref) => {
  const map = useMap();
  const mapsLib = useMapsLibrary('maps');

  const [activeColor, setActiveColor] = useState(COURSE_COLORS[0]);
  const [isDrawingEnabled, setIsDrawingEnabled] = useState(false); // ÚJ: Rajzoló mód kapcsoló
  const [paths, setPaths] = useState([]); // [{ color, points: [{lat, lng}, ...] }]
  const [targets, setTargets] = useState([]); // [{ id, lat, lng }]
  const [specialMarkers, setSpecialMarkers] = useState({ // ÚJ: Speciális jelölők
    office: { lat: null, lng: null },
    warmup: { lat: null, lng: null }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false); // ÚJ: Gomb zöldre váltó állapot

  // ÚJ: KERET ÉS KIVÁGÁS RÖGZÍTÉS ÁLLAPOTOK
  const [isFrameLocked, setIsFrameLocked] = useState(false);
  const [mapCenter, setMapCenter] = useState(null);
  const [mapZoom, setMapZoom] = useState(17);
  const [showInfoModal, setShowInfoModal] = useState(false);

  // ÚJ: KÉPFORGATÁS ÉS TÜKRÖZÉS ÁLLAPOTOK
  const [rotation, setRotation] = useState(0); // 0, 90, 180, 270
  const [flipH, setFlipH] = useState(false); // Vízszintes tükrözés
  const [flipV, setFlipV] = useState(false); // Függőleges tükrözés
  const [isSavingTransform, setIsSavingTransform] = useState(false);

  const handleRotateCW = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const handleRotateCCW = () => {
    setRotation(prev => (prev - 90 + 360) % 360);
  };

  const handleToggleFlipH = () => {
    setFlipH(prev => !prev);
  };

  const handleToggleFlipV = () => {
    setFlipV(prev => !prev);
  };

  const handleResetTransform = () => {
    setRotation(0);
    setFlipH(false);
    setFlipV(false);
  };

  const handleApplyImageTransform = async () => {
    if (!customMapImageUrl) return;

    setIsSavingTransform(true);
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = customMapImageUrl;
      });

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      const isRotated90 = (rotation / 90) % 2 !== 0;
      canvas.width = isRotated90 ? img.height : img.width;
      canvas.height = isRotated90 ? img.width : img.height;

      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);

      const base64Transformed = canvas.toDataURL('image/jpeg', 0.92);

      const publishFunc = httpsCallable(functions, 'publishMapImage');
      const result = await publishFunc({
        eventId: competitionId,
        base64Image: base64Transformed
      });

      const downloadUrl = result.data.imageUrl;
      const collectionName = 'events';

      await updateDoc(doc(db, collectionName, competitionId), {
        customMapImageUrl: downloadUrl,
        ...(isPublishedMap ? { courseMapImageUrl: downloadUrl } : {})
      });

      handleResetTransform();
      alert('Forgatott/tükrözött kép sikeresen elmentve! 🏹💾');
      window.location.reload();
    } catch (error) {
      console.error('Hiba a forgatott kép mentésekor:', error);
      alert('Hiba történt a kép mentése során: ' + (error.message || error));
    } finally {
      setIsSavingTransform(false);
    }
  };

  // EXPORTÁLT FUNKCIÓK A SZÜLŐNEK (Nyomtatáshoz)
  useImperativeHandle(ref, () => ({
    fitAllElements: () => {
      if (!map || !window.google) return;

      if (mapCenter && mapZoom) {
        map.setCenter(mapCenter);
        map.setZoom(mapZoom);
        return;
      }

      const bounds = new window.google.maps.LatLngBounds();
      let hasElements = false;

      // Utak hozzáadása a kerethez
      paths.forEach(path => {
        path.points.forEach(pt => {
          bounds.extend(pt);
          hasElements = true;
        });
      });

      // Célok hozzáadása
      targets.forEach(t => {
        if (t.lat && t.lng) {
          bounds.extend({ lat: t.lat, lng: t.lng });
          hasElements = true;
        }
      });

      // Speciális pontok
      if (specialMarkers.office.lat) {
        bounds.extend(specialMarkers.office);
        hasElements = true;
      }
      if (specialMarkers.warmup.lat) {
        bounds.extend(specialMarkers.warmup);
        hasElements = true;
      }

      if (hasElements) {
        map.fitBounds(bounds, {
            top: 50, right: 50, bottom: 50, left: 50
        });
      }
    }
  }));

  // Össztávolság számítása és visszajelzés a szülőnek
  useEffect(() => {
    let totalDist = 0;
    paths.forEach(path => {
      for (let i = 0; i < path.points.length - 1; i++) {
        totalDist += calculateDistance(
          path.points[i].lat, path.points[i].lng,
          path.points[i+1].lat, path.points[i+1].lng
        );
      }
    });

    if (onDistanceChange) {
      onDistanceChange(Math.round(totalDist));
    }
  }, [paths, onDistanceChange]);

  // Referenciák a Google Maps objektumokhoz
  const polylinesRef = useRef([]);

  // Kezdőpont meghatározása (vagy a verseny GPS-e, vagy Magyarország közepe)
  const initialCenter = useMemo(() => {
    const lat = parseFloat(defaultLat);
    const lng = parseFloat(defaultLng);
    if (!isNaN(lat) && !isNaN(lng) && lat !== 0) {
      return { lat, lng };
    }
    return { lat: 47.1625, lng: 19.5033 };
  }, [defaultLat, defaultLng]);

  // 1. ADATOK BETÖLTÉSE FIRESTORE-BÓL
  useEffect(() => {
    if (!competitionId) return;

    const unsub = onSnapshot(doc(db, 'events', competitionId), (docSnap) => {
      if (docSnap.exists()) {
        const eventData = docSnap.data();
        const data = eventData.courseMapData || {};
        setPaths(data.paths || []);
        setSpecialMarkers(data.specialMarkers || { office: { lat: null, lng: null }, warmup: { lat: null, lng: null } });

        const savedTargets = data.targets || [];
        if (data.mapCenter && data.mapZoom) {
          setMapCenter(data.mapCenter);
          setMapZoom(data.mapZoom);
          setIsFrameLocked(true);
        }

        if (data.updatedAt || (data.paths && data.paths.length > 0)) {
          setIsSaved(true);
        }

        if (savedTargets.length === 0) {
            const initialTargets = Array.from({ length: numberOfTargets || 0 }, (_, i) => ({
                id: i + 1,
                lat: null,
                lng: null
            }));
            setTargets(initialTargets);
        } else {
            // Ha nőt a célszám, hozzáadjuk az újakat
            if (savedTargets.length < numberOfTargets) {
              const updatedTargets = [...savedTargets];
              for (let i = savedTargets.length + 1; i <= numberOfTargets; i++) {
                updatedTargets.push({ id: i, lat: null, lng: null });
              }
              setTargets(updatedTargets);
            } else {
              setTargets(savedTargets);
            }
        }
      }
      setLoading(false);
    });

    return () => unsub();
  }, [competitionId, numberOfTargets]);

  // ELMENTETT TÉRKÉPNÉZET REPOSITIONÁLÁSA BELÉPÉSKOR
  useEffect(() => {
    if (map && mapCenter && mapZoom) {
      map.setCenter(mapCenter);
      map.setZoom(mapZoom);
    }
  }, [map, mapCenter, mapZoom]);

  // 2. VONALAK RAJZOLÁSA A TÉRKÉPRE (GARANTÁLT 100% MEGJELENÍTÉS NYOMTATÁS UTÁN IS)
  const drawPolylines = useCallback(() => {
    if (!map) return;
    const PolylineClass = mapsLib?.Polyline || window.google?.maps?.Polyline;
    if (!PolylineClass) return;

    // Régi vonalak eltávolítása
    polylinesRef.current.forEach(p => p.setMap(null));
    polylinesRef.current = [];

    // Új vonalak létrehozása
    paths.forEach(pathData => {
      const polyline = new PolylineClass({
        path: pathData.points,
        geodesic: true,
        strokeColor: pathData.color,
        strokeOpacity: 0.8,
        strokeWeight: 4,
        map: map
      });
      polylinesRef.current.push(polyline);
    });
  }, [paths, map, mapsLib]);

  useEffect(() => {
    drawPolylines();

    const handleReFocus = () => {
      drawPolylines();
    };

    window.addEventListener('focus', handleReFocus);
    window.addEventListener('visibilitychange', handleReFocus);

    return () => {
      window.removeEventListener('focus', handleReFocus);
      window.removeEventListener('visibilitychange', handleReFocus);
      polylinesRef.current.forEach(p => p.setMap(null));
      polylinesRef.current = [];
    };
  }, [drawPolylines]);

  // 3. KATTINTÁS KEZELÉSE (PONT LERAKÁSA / SNAPPING)
  const handleMapClick = useCallback((e) => {
    if (!isDrawingEnabled || !e.detail.latLng) return;

    setIsSaved(false);

    const newPoint = {
      lat: e.detail.latLng.lat,
      lng: e.detail.latLng.lng
    };

    setPaths(prev => {
      const existingPathIdx = prev.findIndex(p => p.color === activeColor.color);

      if (existingPathIdx >= 0) {
        const updated = [...prev];
        // Ellenőrizzük, hogy nem ugyanaz-e a pont (duplikáció szűrés)
        const lastPoint = updated[existingPathIdx].points[updated[existingPathIdx].points.length - 1];
        if (lastPoint && lastPoint.lat === newPoint.lat && lastPoint.lng === newPoint.lng) {
            return prev;
        }

        updated[existingPathIdx] = {
          ...updated[existingPathIdx],
          points: [...updated[existingPathIdx].points, newPoint]
        };
        return updated;
      } else {
        return [...prev, { color: activeColor.color, points: [newPoint] }];
      }
    });
  }, [isDrawingEnabled, activeColor]);

  // 4. CÉL / SPECIÁLIS JELÖLŐ MOZGATÁSA (0% MAP PANNING RÖGZÍTETT HÁTTÉRNÉL)
  const handleTargetDrag = (id, e, type = 'target') => {
    if (isFrameLocked && map && mapCenter && mapZoom) {
      map.setCenter(mapCenter);
      map.setZoom(mapZoom);
    }
  };

  const handleTargetDragEnd = (id, e, type = 'target') => {
    if (isFrameLocked && map && mapCenter && mapZoom) {
      map.setCenter(mapCenter);
      map.setZoom(mapZoom);
    }

    if (!e || !e.latLng) return;
    const newLat = typeof e.latLng.lat === 'function' ? e.latLng.lat() : parseFloat(e.latLng.lat);
    const newLng = typeof e.latLng.lng === 'function' ? e.latLng.lng() : parseFloat(e.latLng.lng);

    if (isNaN(newLat) || isNaN(newLng)) return;

    setIsSaved(false);

    if (type === 'target') {
      setTargets(prev => prev.map(t =>
        t.id === id ? { ...t, lat: newLat, lng: newLng } : t
      ));
    } else {
      setSpecialMarkers(prev => ({
        ...prev,
        [type]: { lat: newLat, lng: newLng }
      }));
    }
  };

  const handleLockView = () => {
    if (!map) return;
    const centerObj = map.getCenter();
    const currentZoom = map.getZoom() || 17;
    const centerLat = typeof centerObj.lat === 'function' ? centerObj.lat() : centerObj.lat;
    const centerLng = typeof centerObj.lng === 'function' ? centerObj.lng() : centerObj.lng;

    setMapCenter({ lat: centerLat, lng: centerLng });
    setMapZoom(currentZoom);
    setIsFrameLocked(true);
    setIsDrawingEnabled(true);
  };

  // 5. MENTÉS
  const handleSave = async () => {
    setSaving(true);
    try {
      const activeRef = doc(db, 'events', competitionId);
      const activeSnap = await getDoc(activeRef);

      const targetCol = activeSnap.exists() ? 'events' : 'events_closed';
      await updateDoc(doc(db, targetCol, competitionId), {
        courseMapData: {
          paths,
          targets,
          specialMarkers,
          mapCenter,
          mapZoom,
          updatedAt: new Date().toISOString()
        }
      });
      setIsSaved(true);
    } catch (err) {
      console.error("Error saving map:", err);
      alert("Hiba történt a mentés során.");
    } finally {
      setSaving(false);
    }
  };

  // 6. TÖRLÉS / UNDO
  const handleUndo = () => {
    setIsSaved(false);
    setPaths(prev => {
      const idx = prev.findIndex(p => p.color === activeColor.color);
      if (idx < 0) return prev;

      const updated = [...prev];
      const newPoints = [...updated[idx].points];
      newPoints.pop();

      if (newPoints.length === 0) {
        updated.splice(idx, 1);
      } else {
        updated[idx] = { ...updated[idx], points: newPoints };
      }
      return updated;
    });
  };

  const handleClearMap = () => {
    if (window.confirm(`Biztosan törölni szeretnél minden útvonalat és jelölőt a térképről?`)) {
      setIsSaved(false);
      setPaths([]);
      setTargets(prev => prev.map(t => ({ ...t, lat: null, lng: null })));
      setSpecialMarkers({
        office: { lat: null, lng: null },
        warmup: { lat: null, lng: null }
      });
    }
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Térkép adatok betöltése...</div>;

  return (
    <div style={containerStyle} className="force-light-theme map-editor-main-container">

      {/* BAL OLDAL: TÉRKÉP ÉS NÉZET RÖGZÍTŐ SÁV */}
      <div style={mapContainerStyle} className="map-print-container">

        {isCustomMap ? (
          customMapImageUrl ? (
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#1a1a1a',
              borderRadius: '15px',
              position: 'relative',
              padding: '15px',
              boxSizing: 'border-box'
            }}>
              {/* VÁZLAT BADGE BANNER */}
              <div style={{
                position: 'absolute',
                top: '12px',
                left: '12px',
                right: '12px',
                zIndex: 10,
                backgroundColor: isPublishedMap ? 'rgba(40, 167, 69, 0.95)' : 'rgba(255, 243, 205, 0.95)',
                color: isPublishedMap ? '#ffffff' : '#856404',
                border: isPublishedMap ? '1px solid #28a745' : '1px solid #ffeeba',
                borderRadius: '8px',
                padding: '8px 12px',
                fontSize: '0.85rem',
                fontWeight: 'bold',
                boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
                textAlign: 'center'
              }}>
                {isPublishedMap
                  ? '✅ Feltöltött saját térkép PUBLIKÁLVA van (Látható a versenyzőknek)'
                  : '⏳ Feltöltött saját térkép VÁZLAT (A közzétételhez kattints a fenti "Térkép publikálása" gombra)'
                }
              </div>

              {/* KÉP MEGJELENÍTÉSE A TÉRKÉP PONTOS HELYÉN */}
              <img
                src={customMapImageUrl}
                alt="Saját Pályatérkép"
                style={{
                  maxWidth: '100%',
                  maxHeight: 'calc(100% - 50px)',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.4)',
                  marginTop: '30px',
                  transform: `rotate(${rotation}deg) scale(${flipH ? -1 : 1}, ${flipV ? -1 : 1})`,
                  transition: 'transform 0.3s ease'
                }}
              />
            </div>
          ) : (
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#ffffff',
              borderRadius: '15px',
              border: '2px dashed #ccc',
              padding: '30px',
              boxSizing: 'border-box',
              gap: '15px',
              textAlign: 'center'
            }}>
              <FiUpload size={52} color="#b30000" />
              <h3 style={{ margin: 0, color: '#333', fontSize: '1.2rem' }}>Még nincs feltöltve saját térkép!</h3>
              <p style={{ margin: 0, color: '#666', fontSize: '0.9rem', maxWidth: '420px', lineHeight: '1.5' }}>
                Kattints a fenti <strong style={{ color: '#b30000' }}>"Saját térkép feltöltése"</strong> gombra egy képfájl (PNG, JPG, WEBP) kiválasztásához!
              </p>
              <button
                type="button"
                onClick={onUploadNewCustomMap}
                disabled={isUploadingCustomMap}
                style={{
                  padding: '10px 22px',
                  backgroundColor: '#b30000',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 10px rgba(179,0,0,0.25)',
                  marginTop: '5px'
                }}
              >
                <FiUpload size={18} /> {isUploadingCustomMap ? 'Feltöltés...' : 'Saját térkép feltöltése'}
              </button>
            </div>
          )
        ) : (
          <>
            {/* FELSŐ NÉZET-RÖGZÍTŐ VEZÉRLŐSÁV */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: '#ffffff',
              padding: '10px 16px',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              marginBottom: '12px',
              border: '1px solid #e0e0e0',
              position: 'relative',
              zIndex: 10
            }}>
              {!isFrameLocked ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', justifyContent: 'space-between' }}>
                  <button
                    type="button"
                    onClick={handleLockView}
                    style={{
                      backgroundColor: '#2E7D32',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      boxShadow: '0 2px 6px rgba(46,125,50,0.3)'
                    }}
                  >
                    📷 Aktuális térképnézet rögzítése
                  </button>

                  <button
                    type="button"
                    onClick={() => setShowInfoModal(true)}
                    style={{
                      backgroundColor: '#f5f5f5',
                      color: '#007bff',
                      border: '1px solid #007bff',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer'
                    }}
                    title="Útmutató a nézet rögzítéséhez"
                  >
                    <FiInfo size={18} />
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#2E7D32', fontWeight: 'bold', fontSize: '0.9rem' }}>
                    <span>✅ Térképnézet rögzítve! Rajzolás feloldva.</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsFrameLocked(false)}
                    style={{
                      backgroundColor: '#ffffff',
                      color: '#555',
                      border: '1px solid #ccc',
                      borderRadius: '8px',
                      padding: '6px 12px',
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    ✏️ Kivágás módosítása
                  </button>
                </div>
              )}
            </div>

            <Map
              defaultCenter={initialCenter}
              defaultZoom={17}
              mapId="COURSE_EDITOR_MAP"
              mapTypeId="satellite"
              onClick={handleMapClick}
              style={{ width: '100%', height: '100%' }}
              gestureHandling={isFrameLocked ? 'none' : 'greedy'}
              disableDefaultUI={true}
            >
              {/* CÉL JELÖLŐK RENDERE */}
              {targets.map(target => (
                target.lat && (
                  <AdvancedMarker
                    key={`target-${target.id}`}
                    position={{ lat: target.lat, lng: target.lng }}
                    draggable={!isDrawingEnabled}
                    onDrag={(e) => handleTargetDrag(target.id, e, 'target')}
                    onDragEnd={(e) => handleTargetDragEnd(target.id, e, 'target')}
                  >
                    <div style={targetMarkerStyle}>
                      <div style={targetLabelStyle}>{target.id}</div>
                      <div style={pinPointStyle} />
                    </div>
                  </AdvancedMarker>
                )
              ))}

              {/* SPECIÁLIS JELÖLŐK: IRODA */}
              {specialMarkers.office.lat && (
                <AdvancedMarker
                  position={specialMarkers.office}
                  draggable={!isDrawingEnabled}
                  onDrag={(e) => handleTargetDrag('office', e, 'office')}
                  onDragEnd={(e) => handleTargetDragEnd('office', e, 'office')}
                >
                  <div style={targetMarkerStyle}>
                    <div style={{ ...targetLabelStyle, color: '#007bff', borderColor: '#007bff' }}>IRODA</div>
                    <div style={{ ...pinPointStyle, backgroundColor: '#007bff' }}>
                      <FiHome size={10} color="white" style={{ transform: 'rotate(45deg)', marginBottom: '4px' }} />
                    </div>
                  </div>
                </AdvancedMarker>
              )}

              {/* SPECIÁLIS JELÖLŐK: BELÖVŐ */}
              {specialMarkers.warmup.lat && (
                <AdvancedMarker
                  position={specialMarkers.warmup}
                  draggable={!isDrawingEnabled}
                  onDrag={(e) => handleTargetDrag('warmup', e, 'warmup')}
                  onDragEnd={(e) => handleTargetDragEnd('warmup', e, 'warmup')}
                >
                  <div style={targetMarkerStyle}>
                    <div style={{ ...targetLabelStyle, color: '#333', borderColor: '#333' }}>BELÖVŐ</div>
                    <div style={{ ...pinPointStyle, backgroundColor: '#333' }}>
                      <FiCrosshair size={10} color="white" style={{ transform: 'rotate(45deg)', marginBottom: '4px' }} />
                    </div>
                  </div>
                </AdvancedMarker>
              )}

              {/* ÚJ: ÚTVONAL PONTOK (VERTICES) MEGJELENÍTÉSE RAJZOLÁS KÖZBEN */}
              {isDrawingEnabled && paths.map((pathData, pIdx) => (
                pathData.points.map((point, ptIdx) => (
                  <AdvancedMarker
                    key={`vertex-${pIdx}-${ptIdx}`}
                    position={point}
                    zIndex={100}
                    onClick={(e) => {
                      if (e.domEvent) {
                        e.domEvent.stopPropagation();
                        e.domEvent.preventDefault();
                      }
                      handleMapClick({ detail: { latLng: point } });
                    }}
                  >
                    <div style={{
                      width: '14px',
                      height: '14px',
                      backgroundColor: pathData.color,
                      borderRadius: '50%',
                      border: '2px solid white',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      transform: 'translate(-50%, -50%)',
                      cursor: 'pointer'
                    }} />
                  </AdvancedMarker>
                ))
              ))}
            </Map>

            {/* TÁVOLSÁG KIJELZŐ A TÉRKÉPEN */}
            {totalDistance > 0 && (
              <div style={distanceOverlayStyle}>
                Teljes táv: <strong>{totalDistance} m</strong>
              </div>
            )}

            {/* GYORSGOMBOK A TÉRKÉPEN (Csak akkor láthatók, ha a nézet már rögzítve van) */}
            {isFrameLocked && (
              <div style={mapOverlayControlsStyle}>
                  <button style={toolButtonStyle} onClick={handleUndo}>
                      <FiRotateCcw /> Vissza
                  </button>
                  <button style={toolButtonStyle} onClick={handleClearMap}>
                      <FiTrash2 /> Térkép ürítése
                  </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* JOBB OLDAL: KEZELŐPANEL */}
      <div style={{ ...sidebarStyle, opacity: (isCustomMap || isFrameLocked) ? 1 : 0.4, pointerEvents: (isCustomMap || isFrameLocked) ? 'auto' : 'none' }}>

        {isCustomMap ? (
          customMapImageUrl ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', height: '100%' }}>
              <button
                type="button"
                onClick={onUploadNewCustomMap}
                disabled={isUploadingCustomMap}
                style={{
                  ...customMapButtonStyle,
                  backgroundColor: '#b30000',
                  color: 'white',
                  borderColor: '#b30000',
                  padding: '9px 12px'
                }}
              >
                <FiUpload size={15} /> {isUploadingCustomMap ? 'Feltöltés...' : 'Új saját térkép feltöltése'}
              </button>

              <div style={dividerStyle} />

              {/* KÉPFORGATÓ ÉS TÜKRÖZŐ ESZKÖZÖK */}
              <div style={panelSectionStyle}>
                <div style={panelLabelStyle}>Forgatás és Tükrözés</div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                  <button
                    type="button"
                    onClick={handleRotateCCW}
                    style={customMapButtonStyle}
                    title="90° Balra forgatás"
                  >
                    ↺ 90° Balra
                  </button>
                  <button
                    type="button"
                    onClick={handleRotateCW}
                    style={customMapButtonStyle}
                    title="90° Jobbra forgatás"
                  >
                    ↻ 90° Jobbra
                  </button>
                  <button
                    type="button"
                    onClick={handleToggleFlipH}
                    style={{
                      ...customMapButtonStyle,
                      backgroundColor: flipH ? '#e2e8f0' : '#fff',
                      borderColor: flipH ? '#4a5568' : '#ccc'
                    }}
                    title="Vízszintes tükrözés"
                  >
                    ↔ Vízszintes
                  </button>
                  <button
                    type="button"
                    onClick={handleToggleFlipV}
                    style={{
                      ...customMapButtonStyle,
                      backgroundColor: flipV ? '#e2e8f0' : '#fff',
                      borderColor: flipV ? '#4a5568' : '#ccc'
                    }}
                    title="Függőleges tükrözés"
                  >
                    ↕ Függőleges
                  </button>
                </div>

                {(rotation !== 0 || flipH || flipV) && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '6px' }}>
                    <button
                      type="button"
                      onClick={handleApplyImageTransform}
                      disabled={isSavingTransform}
                      style={{
                        ...customMapButtonStyle,
                        backgroundColor: '#28a745',
                        color: 'white',
                        borderColor: '#28a745',
                        fontWeight: 'bold'
                      }}
                    >
                      <FiSave size={15} /> {isSavingTransform ? 'Mentés...' : 'Módosított kép mentése'}
                    </button>

                    <button
                      type="button"
                      onClick={handleResetTransform}
                      style={{
                        ...customMapButtonStyle,
                        backgroundColor: '#f8f9fa',
                        color: '#666',
                        borderColor: '#ddd'
                      }}
                    >
                      <FiRotateCcw size={15} /> Alaphelyzet (Mégse)
                    </button>
                  </div>
                )}
              </div>

              <div style={dividerStyle} />

              <button
                type="button"
                onClick={() => window.open(customMapImageUrl, '_blank')}
                style={{
                  ...customMapButtonStyle,
                  backgroundColor: '#f8f9fa',
                  color: '#333',
                  borderColor: '#ddd'
                }}
              >
                <FiImage size={15} /> Teljes méretű kép
              </button>

              <div style={dividerStyle} />

              <button
                type="button"
                onClick={onRemoveCustomMap}
                style={{
                  ...customMapButtonStyle,
                  backgroundColor: '#fff0f0',
                  color: '#b30000',
                  borderColor: '#ffcccc',
                  marginTop: 'auto'
                }}
              >
                <FiTrash2 size={15} /> Saját térkép törlése
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', height: '100%' }}>
              <div style={panelSectionStyle}>
                <div style={panelLabelStyle}>Saját térkép modul</div>
                <p style={{ fontSize: '0.8rem', color: '#666', lineHeight: '1.4', margin: '4px 0 0 0' }}>
                  A saját térkép modul kiválasztásával feltölthetsz egy saját egyedi pályatérkép képet.
                </p>
              </div>

              <div style={dividerStyle} />

              <button
                type="button"
                onClick={onUploadNewCustomMap}
                disabled={isUploadingCustomMap}
                style={{
                  ...customMapButtonStyle,
                  backgroundColor: '#b30000',
                  color: 'white',
                  borderColor: '#b30000',
                  padding: '10px'
                }}
              >
                <FiUpload size={16} /> {isUploadingCustomMap ? 'Feltöltés...' : 'Saját térkép feltöltése'}
              </button>

              <button
                type="button"
                onClick={onSwitchToGoogleMaps}
                style={{
                  ...customMapButtonStyle,
                  backgroundColor: '#f8f9fa',
                  color: '#555',
                  borderColor: '#ddd'
                }}
              >
                <FiMap size={15} /> Vissza a Google Maps-re
              </button>
            </div>
          )
        ) : (
          <>
            {/* ÜZEMMÓD VÁLASZTÓ */}
            <div style={panelSectionStyle}>
              <div style={panelLabelStyle}>Munkamód</div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setIsDrawingEnabled(true)}
                  style={{
                    ...modeButtonStyle,
                    backgroundColor: isDrawingEnabled ? '#b30000' : '#f5f5f5',
                    color: isDrawingEnabled ? '#fff' : '#666',
                    borderColor: isDrawingEnabled ? '#b30000' : '#ddd'
                  }}
                >
                  <FiMap size={16} /> Rajzolás
                </button>
                <button
                  onClick={() => setIsDrawingEnabled(false)}
                  style={{
                    ...modeButtonStyle,
                    backgroundColor: !isDrawingEnabled ? '#2E7D32' : '#f5f5f5',
                    color: !isDrawingEnabled ? '#fff' : '#666',
                    borderColor: !isDrawingEnabled ? '#2E7D32' : '#ddd'
                  }}
                >
                  <FiTarget size={16} /> Célok
                </button>
              </div>
            </div>

            <div style={dividerStyle} />

            {/* SZÍNVÁLASZTÓ */}
            <div style={panelSectionStyle}>
              <div style={panelLabelStyle}>Pálya színe:</div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {COURSE_COLORS.map(c => (
                  <div
                    key={c.id}
                    onClick={() => setActiveColor(c)}
                    style={{
                      ...colorCircleStyle,
                      backgroundColor: c.color,
                      border: activeColor.id === c.id ? '4px solid #333' : '2px solid transparent',
                      transform: activeColor.id === c.id ? 'scale(1.1)' : 'none'
                    }}
                    title={c.label}
                  />
                ))}
              </div>
              <div style={{ fontSize: '0.8rem', fontWeight: 'bold', color: activeColor.color }}>
                 {activeColor.label} rajzolása aktív
              </div>
            </div>

            <div style={dividerStyle} />

            {/* SPECIÁLIS JELÖLŐK LISTÁJA */}
            <div style={panelSectionStyle}>
              <div style={panelLabelStyle}>Speciális helyszínek:</div>
              <div style={{ display: 'flex', gap: '8px' }}>
                 <button
                    onClick={() => {
                      if (!map) return;
                      const center = map.getCenter();
                      if (!center) return;
                      const lat = typeof center.lat === 'function' ? center.lat() : center.lat;
                      const lng = typeof center.lng === 'function' ? center.lng() : center.lng;
                      handleTargetDragEnd('office', { latLng: { lat, lng } }, 'office');
                    }}
                    style={{
                      ...targetItemStyle,
                      backgroundColor: specialMarkers.office.lat ? '#E3F2FD' : '#f5f5f5',
                      borderColor: specialMarkers.office.lat ? '#007bff' : '#ddd',
                      color: specialMarkers.office.lat ? '#007bff' : '#666',
                      flex: 1
                    }}
                 >
                    🏢 Versenyiroda
                 </button>
                 <button
                    onClick={() => {
                      if (!map) return;
                      const center = map.getCenter();
                      if (!center) return;
                      const lat = typeof center.lat === 'function' ? center.lat() : center.lat;
                      const lng = typeof center.lng === 'function' ? center.lng() : center.lng;
                      handleTargetDragEnd('warmup', { latLng: { lat, lng } }, 'warmup');
                    }}
                    style={{
                      ...targetItemStyle,
                      backgroundColor: specialMarkers.warmup.lat ? '#EEEEEE' : '#f5f5f5',
                      borderColor: specialMarkers.warmup.lat ? '#333' : '#ddd',
                      color: specialMarkers.warmup.lat ? '#333' : '#666',
                      flex: 1
                    }}
                 >
                    🏹 Belövő pálya
                 </button>
              </div>
            </div>

            {/* CÉLOK LISTÁJA */}
            <div style={{ ...panelSectionStyle, flex: 1, overflow: 'hidden' }}>
              <div style={panelLabelStyle}>Célok ({numberOfTargets} db):</div>
              <div style={targetsListStyle}>
                {targets.map(t => (
                  <div
                    key={t.id}
                    onClick={() => {
                        if (isDrawingEnabled) return;
                        if (!t.lat) {
                            const center = map.getCenter();
                            handleTargetDragEnd(t.id, { latLng: center }, 'target');
                        } else {
                            map.panTo({ lat: t.lat, lng: t.lng });
                        }
                    }}
                    style={{
                      ...targetItemStyle,
                      backgroundColor: t.lat ? '#E8F5E9' : '#f5f5f5',
                      borderColor: t.lat ? '#2E7D32' : '#ddd',
                      color: t.lat ? '#2E7D32' : '#666',
                      opacity: isDrawingEnabled ? 0.5 : 1,
                      textAlign: 'center',
                      minWidth: '60px'
                    }}
                  >
                    {t.id}.
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                ...saveButtonStyle,
                backgroundColor: isSaved ? '#2E7D32' : '#b30000',
                opacity: saving ? 0.7 : 1,
                transition: 'background-color 0.3s ease'
              }}
            >
              {saving ? 'Mentés...' : (isSaved ? '✅ Térkép mentve!' : <><FiSave /> Térkép Mentése</>)}
            </button>
          </>
        )}
      </div>

      {/* INFO POPUP MODAL */}
      {showInfoModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 100000
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '450px',
            width: '90%',
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
            textAlign: 'center'
          }}>
            <h3 style={{ margin: '0 0 12px 0', color: '#b30000', fontSize: '1.2rem' }}>
              📷 Térképnézet Rögzítése Útmutató
            </h3>
            <p style={{ fontSize: '0.95rem', color: '#333', lineHeight: '1.6', marginBottom: '20px' }}>
              <strong>1. Lépés:</strong> Mozgasd és nagyítsd/kicsinyítsd a műholdas térképet a kívánt pozícióra és tökéletes keretre!<br/><br/>
              <strong>2. Lépés:</strong> Kattints az <strong>"Aktuális térképnézet rögzítése"</strong> gombra!<br/><br/>
              <strong>3. Lépés:</strong> A nézet rögzítése után feloldódnak a rajzoló eszközök, és elkezdheted az utak meg a célok berajzolását.
            </p>
            <button
              type="button"
              onClick={() => setShowInfoModal(false)}
              style={{
                backgroundColor: '#b30000',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 24px',
                fontWeight: 'bold',
                fontSize: '0.9rem',
                cursor: 'pointer'
              }}
            >
              Értem!
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

const containerStyle = {
  display: 'flex',
  flexDirection: 'row',
  height: 'calc(100vh - 180px)',
  gap: '20px',
  backgroundColor: '#f8f9fa'
};

const mapContainerStyle = {
  flex: 1,
  borderRadius: '15px',
  overflow: 'hidden',
  border: '1px solid #ddd',
  position: 'relative',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
};

const sidebarStyle = {
  width: '320px',
  backgroundColor: '#fff',
  padding: '20px',
  borderRadius: '15px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  display: 'flex',
  flexDirection: 'column',
  gap: '15px',
  overflowY: 'auto'
};

const dividerStyle = {
  height: '1px',
  backgroundColor: '#eee',
  margin: '5px 0'
};

const panelSectionStyle = { display: 'flex', flexDirection: 'column', gap: '8px' };
const panelLabelStyle = { fontSize: '0.7rem', fontWeight: 'bold', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' };
const colorCircleStyle = { width: '28px', height: '28px', borderRadius: '50%', cursor: 'pointer', transition: 'all 0.2s', border: '2px solid transparent' };
const targetsListStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(65px, 1fr))',
  gap: '8px',
  padding: '5px',
  overflowY: 'auto'
};

const customMapButtonStyle = {
  width: '100%',
  padding: '6px 12px',
  borderRadius: '8px',
  border: '1px solid',
  fontWeight: 'bold',
  fontSize: '0.8rem',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  transition: 'all 0.2s'
};

const modeButtonStyle = {
  flex: 1,
  padding: '10px',
  borderRadius: '10px',
  border: '2px solid',
  fontWeight: 'bold',
  fontSize: '0.85rem',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  transition: 'all 0.2s'
};

const targetItemStyle = {
  padding: '8px',
  borderRadius: '10px',
  border: '2px solid',
  fontSize: '0.85rem',
  fontWeight: 'bold',
  cursor: 'pointer',
  transition: 'all 0.2s',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const saveButtonStyle = {
  backgroundColor: '#b30000',
  color: 'white',
  border: 'none',
  padding: '15px',
  borderRadius: '12px',
  fontWeight: 'bold',
  fontSize: '1rem',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '10px',
  marginTop: 'auto',
  boxShadow: '0 4px 12px rgba(179,0,0,0.3)'
};

const mapOverlayControlsStyle = {
  position: 'absolute',
  bottom: '20px',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: '10px',
  zIndex: 1
};

const toolButtonStyle = {
    backgroundColor: 'rgba(255,255,255,0.95)',
    border: '1px solid #ddd',
    padding: '10px 18px',
    borderRadius: '10px',
    fontSize: '0.9rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
};

const distanceOverlayStyle = {
    position: 'absolute',
    top: '70px',
    right: '20px',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    color: '#b30000',
    padding: '8px 15px',
    borderRadius: '10px',
    fontSize: '1rem',
    fontWeight: 'bold',
    boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
    zIndex: 5,
    border: '2px solid #b30000'
};

const targetMarkerStyle = { display: 'flex', flexDirection: 'column', alignItems: 'center', transform: 'translateY(-50%)' };

const targetLabelStyle = {
  backgroundColor: '#fff',
  padding: '2px 8px',
  borderRadius: '12px',
  fontSize: '0.8rem',
  fontWeight: '900',
  color: '#b30000',
  border: '2px solid #b30000',
  marginBottom: '2px',
  boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
  zIndex: 1
};

const pinPointStyle = {
    width: '16px',
    height: '16px',
    backgroundColor: '#D32F2F',
    borderRadius: '50% 50% 50% 0',
    transform: 'rotate(-45deg) translateY(-2px) translateX(2px)',
    border: '2px solid white',
    boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
    marginTop: '-4px'
};

export default CourseMapEditor;
